import { useTRPC, useTRPCClient } from "#trpc";
import { TextDisplayWidget } from "#components/display";
import { humanizeNumericValue } from "@dmca/utils/functions";
import type { IPlant } from "@dmca/models/interface/plant";
import { PulseMeterResourceTypeEnum } from "@dmca/models/interface/pulse-meter";
import { DateTime } from "@dmca/utils/luxon";
import ElectricBoltIcon from "@mui/icons-material/ElectricBolt";
import GasMeterIcon from "@mui/icons-material/GasMeter";
import WaterDropIcon from "@mui/icons-material/WaterDrop";

import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { Component, Suspense, lazy, useState, type ReactNode } from "react";
import { IPulseMeter } from "@dmca/models/interface/pulse-meter";
import { formatMeasurementUnit } from "./formatMeasurementUnit";

class ChartErrorBoundary extends Component<
  { children: ReactNode; label: string },
  { hasError: boolean }
> {
  constructor(props: { children: ReactNode; label: string }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  override render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          <Typography color="error" variant="body2">
            Failed to load {this.props.label} — try refreshing
          </Typography>
        </Box>
      );
    }
    return this.props.children;
  }
}

const PowerUsagePulseMeterLineGraph = lazy(
  () => import("./PowerUsagePulseMeterLineGraph")
);

export interface PowerUsagePlantSectionProps {
  plant: IPlant;
  cellIds: string[];
  timeRange: {
    start: number | DateTime;
    end: number | DateTime;
  };
  showPulseMeters?: boolean;
  canViewHydroBaseline?: boolean;
  canEditHydroBaseline?: boolean;
}

const chartLoadingFallback = (
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100%",
    }}
  >
    <CircularProgress />
  </Box>
);

function pulseMeterDisplayValue(
  value: number | null | undefined,
  meter: IPulseMeter,
  opts?: { includeTimeUnit?: boolean }
) {
  if (value == null || !Number.isFinite(value)) return "Unavailable";
  const valueUnit = formatMeasurementUnit(meter.valueMeasurementType);
  const unit = opts?.includeTimeUnit
    ? `${valueUnit}/${meter.timeMeasurementType}`
    : valueUnit;
  return humanizeNumericValue(value, unit, {
    precision: 2,
    displaySeparator: true,
  });
}

export const PowerUsagePlantSection: React.FC<PowerUsagePlantSectionProps> = ({
  plant,
  cellIds,
  timeRange,
  showPulseMeters,
  canViewHydroBaseline,
  canEditHydroBaseline,
}) => {
  const trpc = useTRPC();
  const trpcClient = useTRPCClient();

  const metersQuery = useQuery({
    ...trpc.pulseMeter.getMultiple.queryOptions({
      byLocationIds: cellIds,
    }),
    enabled: !!showPulseMeters,
  });
  const pulseMeters = metersQuery.data;
  const pulseMeterQueryLoading = !!showPulseMeters && metersQuery.isLoading;

  const pulseMeterIds = pulseMeters?.map((meter) => meter.id) ?? [];

  const usageQuery = useQuery({
    queryKey: trpc.pulseMeter.getCurrentUsage.queryKey({ pulseMeterIds }),
    queryFn: () => {
      const now = DateTime.now();
      return trpcClient.pulseMeter.getCurrentUsage.query({
        pulseMeterIds,
        start: now.startOf("day"),
        end: now,
      });
    },
    enabled: !!showPulseMeters && pulseMeterIds.length > 0,
    refetchInterval: 5_000,
  });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="column" gap={2}>
        <Typography variant="h6" data-testid="power-plant-name">
          {plant.name}
        </Typography>
        <Divider />
        <Typography variant="body2" color="text.secondary">
          Rates estimate the last recorded pulse interval. An unavailable rate
          does not establish zero flow or device status; consumption may remain
          below the next pulse increment.
        </Typography>

        {metersQuery.isError ? (
          <Typography role="status">Pulse meter data unavailable</Typography>
        ) : pulseMeterQueryLoading ? (
          <CircularProgress />
        ) : !pulseMeters?.length ? (
          <Typography
            color="text.secondary"
            variant="body2"
            data-testid="power-pulse-meter-empty"
          >
            No pulse meters found for this plant.
          </Typography>
        ) : (
          <Stack direction="column" gap={2}>
            {pulseMeters
              ?.slice()
              .sort(
                (a, b) =>
                  ["electricity", "water", "gas"].indexOf(a.resourceType) -
                  ["electricity", "water", "gas"].indexOf(b.resourceType)
              )
              .map((meter) => (
                <PulseMeterSection
                  key={meter.id}
                  meter={meter}
                  usage={
                    usageQuery.isError ? undefined : usageQuery.data?.[meter.id]
                  }
                  timeRange={timeRange}
                  canViewHydroBaseline={canViewHydroBaseline}
                  canEditHydroBaseline={canEditHydroBaseline}
                />
              ))}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

function PulseMeterSection({
  meter,
  usage,
  timeRange,
  canViewHydroBaseline,
  canEditHydroBaseline,
}: {
  meter: IPulseMeter;
  usage?: { immediateValue: number | null; totalValue: number };
  timeRange: PowerUsagePlantSectionProps["timeRange"];
  canViewHydroBaseline?: boolean;
  canEditHydroBaseline?: boolean;
}) {
  const trpc = useTRPC();
  const isHydro = meter.resourceType === PulseMeterResourceTypeEnum.ELECTRICITY;
  const [showBaseline, setShowBaseline] = useState(false);
  const [activeMinutes, setActiveMinutes] = useState<number | null>(null);
  const baselineQuery = useQuery({
    ...trpc.pulseMeter.getHydroBaseline.queryOptions({
      pulseMeterId: meter.id,
    }),
    enabled: isHydro && !!canViewHydroBaseline,
    staleTime: 5 * 60 * 1000,
  });
  const baseline =
    canViewHydroBaseline && !baselineQuery.isError
      ? baselineQuery.data
      : undefined;
  const icon =
    meter.resourceType === PulseMeterResourceTypeEnum.ELECTRICITY ? (
      <ElectricBoltIcon color="warning" />
    ) : meter.resourceType === PulseMeterResourceTypeEnum.WATER ? (
      <WaterDropIcon color="info" />
    ) : (
      <GasMeterIcon color="action" />
    );
  return (
    <>
      <Divider />
      <Stack direction="row" alignItems="center" gap={1}>
        {icon}
        <Typography variant="subtitle1" fontWeight={600}>
          {meter.name}
        </Typography>
      </Stack>
      {isHydro && canViewHydroBaseline && baselineQuery.isLoading && (
        <CircularProgress aria-label="Loading Hydro baseline" />
      )}
      {isHydro && canViewHydroBaseline && baselineQuery.isError && (
        <Stack direction="row" alignItems="center" gap={1}>
          <Typography color="error" role="alert" variant="body2">
            Could not load Hydro baseline.
          </Typography>
          <Button
            size="small"
            disabled={baselineQuery.isFetching}
            onClick={() => void baselineQuery.refetch()}
          >
            Retry baseline
          </Button>
          {baselineQuery.isFetching && (
            <CircularProgress size={20} aria-label="Loading Hydro baseline" />
          )}
        </Stack>
      )}
      <Stack direction="row" gap={1} flexWrap="wrap">
        <TextDisplayWidget
          label={`${meter.name} (Recorded Usage Today)`}
          helpText="Total usage recorded by this meter from local midnight to now, based on received pulses."
          sx={{ flex: "1 1 200px" }}
        >
          {pulseMeterDisplayValue(usage?.totalValue, meter)}
        </TextDisplayWidget>
        <TextDisplayWidget
          label={`${meter.name} (Recent Pulse Rate)`}
          helpText="Estimated usage rate from the latest pulse interval. It is unavailable when recent pulses cannot establish a rate."
          sx={{ flex: "1 1 200px" }}
        >
          {pulseMeterDisplayValue(usage?.immediateValue, meter, {
            includeTimeUnit: true,
          })}
        </TextDisplayWidget>
        {isHydro && canViewHydroBaseline && showBaseline && (
          <TextDisplayWidget
            label="Active time (selected range)"
            helpText="Estimated time above the Hydro baseline within the selected graph range. To see active time today, select a range from midnight to now."
            sx={{ flex: "1 1 200px" }}
          >
            {baselineQuery.isError
              ? "Baseline unavailable"
              : baseline?.value == null || activeMinutes == null
              ? "No baseline data"
              : `${(activeMinutes / 60).toFixed(1)} hours`}
          </TextDisplayWidget>
        )}
      </Stack>
      <Box sx={{ minHeight: 300 }}>
        <ChartErrorBoundary label={`${meter.name} chart`}>
          <Suspense fallback={chartLoadingFallback}>
            <PowerUsagePulseMeterLineGraph
              pulseMeter={meter}
              timeRange={timeRange}
              baseline={isHydro ? baseline : undefined}
              showBaseline={showBaseline}
              onShowBaselineChange={setShowBaseline}
              onActiveMinutesChange={setActiveMinutes}
              canEditBaseline={
                isHydro && !!canViewHydroBaseline && !!canEditHydroBaseline
              }
            />
          </Suspense>
        </ChartErrorBoundary>
      </Box>
    </>
  );
}
